// src/components/ContactTabsForm.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import AOS from 'aos';
import 'aos/dist/aos.css';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.section`
  border: 1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
  border-radius: ${({theme})=>theme.radius.xl}px;
  box-shadow: ${({theme})=>theme.shadow.sm};
  overflow: hidden;
`;

const Tabs = styled.div`
  display:flex; flex-wrap: wrap; gap: 8px; padding: 14px; border-bottom: 1px solid ${({theme})=>theme.border};
  button{
    padding: 10px 14px; border-radius: ${({theme})=>theme.radius.pill}px; cursor: pointer;
    background: ${({theme})=>theme.body}; color: ${({theme})=>theme.text}; border: 1px solid ${({theme})=>theme.border};
    transition: .2s transform ease;
  }
  button[aria-selected="true"]{
    background: ${({theme})=>theme.surface}; border-color: ${({theme})=>theme.accent};
    box-shadow: 0 0 0 3px color-mix(in srgb, ${({theme})=>theme.accent} 24%, transparent);
  }
  button:active{ transform: translateY(1px); }
`;

const Body = styled.form`
  padding: 18px; display:grid; grid-template-columns: 1fr 1fr; gap: 14px;
  @media (max-width: 880px){ grid-template-columns: 1fr; }
  .full{ grid-column: 1 / -1; }
  label{ display:grid; gap: 6px; font-size: 14px; }
  input, textarea, select{
    border: 1px solid ${({theme})=>theme.border};
    background: ${({theme})=>theme.body};
    color: ${({theme})=>theme.text};
    border-radius: ${({theme})=>theme.radius.md}px;
    padding: 10px 12px;
    outline: none;
  }
  textarea{ min-height: 140px; resize: vertical; }
  .row{ display:flex; gap: 10px; flex-wrap: wrap; }
  .hint{ font-size: 12px; color: ${({theme})=>theme.textMuted}; }
  .error{ color: #d33; font-size: 13px; }
  .ok{ color: #2d8a34; font-size: 13px; }
  animation: ${fadeUp} .4s ease both;
`;

const Drop = styled.div`
  border: 2px dashed ${({theme})=>theme.border};
  background:
    radial-gradient(120px 60px at 10% 0%, color-mix(in srgb, ${({theme})=>theme.accent} 10%, transparent), transparent),
    ${({theme})=>theme.body};
  color: ${({theme})=>theme.textMuted};
  border-radius: ${({theme})=>theme.radius.lg}px;
  padding: 14px;
  display:grid; gap: 8px;
  text-align: center;
  transition: .2s ease;
  &.drag{
    border-color: ${({theme})=>theme.accent};
    background:
      radial-gradient(120px 60px at 10% 0%, color-mix(in srgb, ${({theme})=>theme.accent} 18%, transparent), transparent),
      ${({theme})=>theme.body};
    color: ${({theme})=>theme.text};
  }
  input[type=file]{ display:none; }
  .files{ display:flex; gap: 8px; flex-wrap: wrap; justify-content:center; }
  .chip{
    border: 1px solid ${({theme})=>theme.border}; background: ${({theme})=>theme.surface};
    color: ${({theme})=>theme.text}; border-radius: ${({theme})=>theme.radius.pill}px;
    padding: 6px 10px; display:inline-flex; align-items:center; gap:8px;
  }
  .chip b{ font-weight: 600; }
  .chip button{
    border: 0; background: none; color: ${({theme})=>theme.textMuted}; cursor:pointer; padding: 0 2px;
  }
`;

const Footer = styled.div`
  display:flex; justify-content: space-between; align-items:center; gap: 12px;
  border-top: 1px solid ${({theme})=>theme.border};
  padding: 12px 14px; flex-wrap: wrap;
`;

const Submit = styled.button`
  padding: 10px 16px; border-radius: ${({theme})=>theme.radius.pill}px; cursor: pointer;
  background: ${({theme})=>theme.accent}; color: #fff; border: 1px solid ${({theme})=>theme.accent};
  font-weight: 700; letter-spacing: .02em;
  &:disabled{ opacity:.6; cursor:not-allowed; }
`;

const departments = [
  {key:'sales',    emoji:'💼'},
  {key:'purchase', emoji:'🛒'},
  {key:'warehouse',emoji:'🏷️'},
  {key:'mebli',    emoji:'🪑'},
  {key:'metal',    emoji:'⚙️'},
  {key:'account',  emoji:'🧾'},
  {key:'legal',    emoji:'⚖️'},
  {key:'service',  emoji:'🛠️'},
  {key:'other',    emoji:'✉️'},
];

const MAX_FILES = 6;
const MAX_SIZE_MB = 25;
const ACCEPT = [
  'image/png','image/jpeg','image/webp','application/pdf',
  'application/zip','application/x-zip-compressed',
  'application/vnd.ms-excel','application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // чертежи
  'image/svg+xml','application/vnd.dwg','application/acad','application/dxf','application/x-dxf'
];

export default function ContactTabsForm() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('sales');
  const [data, setData] = useState({
    name:'', email:'', phone:'', subject:'', message:'', agree:false
  });
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const inputRef = useRef(null);

  useEffect(()=>{ AOS.init({once:true, duration:600}); },[]);

  const acceptStr = useMemo(()=>ACCEPT.join(','), []);

  const onPick = (list) => {
    const arr = Array.from(list||[]);
    if (!arr.length) return;

    const next = [];
    let rejected = 0;

    for (const f of arr) {
      const okType = ACCEPT.includes(f.type) || f.name.match(/\.(dwg|dxf|step|stp|iges|igs|zip|rar|7z)$/i);
      const okSize = f.size <= MAX_SIZE_MB*1024*1024;
      if (!okType || !okSize) { rejected++; continue; }
      if (files.length + next.length >= MAX_FILES) break;
      next.push(f);
    }
    const merged = [...files, ...next];
    setFiles(merged);
    if (rejected) setNote({type:'error', text: t('contact.filesRejected', {count:rejected})});
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_,i)=>i!==idx));

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    onPick(e.dataTransfer.files);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setNote(null);

    if (!data.name || (!data.email && !data.phone) || !data.message) {
      setNote({type:'error', text: t('contact.fillRequired')});
      return;
    }
    if (!data.agree) {
      setNote({type:'error', text: t('contact.agreeRequired')});
      return;
    }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('dept', tab);
      Object.entries(data).forEach(([k,v])=>fd.append(k, String(v)));
      files.forEach((f,i)=>fd.append('files', f, f.name));

      // Замените эндпоинт на ваш
      const res = await fetch('/api/contact', { method:'POST', body: fd });
      if (!res.ok) throw new Error('Failed');

      setNote({type:'ok', text: t('contact.sent')});
      setData({name:'', email:'', phone:'', subject:'', message:'', agree:false});
      setFiles([]);
    } catch(err){
      // fallback: открываем почтовый клиент (вложения почтовой ссылкой не прикрепить)
      setNote({type:'error', text: t('contact.sendError')});
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrap data-aos="fade-up">
      {/* ВКЛАДКИ */}
      <Tabs role="tablist" aria-label={t('contact.tabs')}>
        {departments.map(d=>(
          <button
            key={d.key}
            role="tab"
            type="button"
            aria-selected={tab===d.key}
            aria-controls={`panel-${d.key}`}
            id={`tab-${d.key}`}
            onClick={()=>setTab(d.key)}
            title={t(`depart.${d.key}.title`, d.key)}
          >
            <span style={{marginRight:6}} aria-hidden>{d.emoji}</span>
            {t(`depart.${d.key}.short`, d.key.toUpperCase())}
          </button>
        ))}
      </Tabs>

      {/* ФОРМА */}
      <Body onSubmit={onSubmit} noValidate aria-labelledby={`tab-${tab}`} id={`panel-${tab}`} role="tabpanel">
        <label>
          {t('contact.name')}*
          <input
            name="name" autoComplete="name" placeholder={t('contact.place.name')}
            value={data.name} onChange={e=>setData(s=>({...s, name: e.target.value}))}
            required
          />
        </label>

        <label>
          Email / {t('contact.phone')}*
          <div className="row">
            <input
              name="email" type="email" autoComplete="email" placeholder="you@email.com"
              value={data.email} onChange={e=>setData(s=>({...s, email: e.target.value}))}
            />
            <input
              name="phone" autoComplete="tel" placeholder="+38 (0XX) XXX-XX-XX"
              value={data.phone} onChange={e=>setData(s=>({...s, phone: e.target.value}))}
            />
          </div>
          <span className="hint">{t('contact.anyOne')}</span>
        </label>

        <label className="full">
          {t('contact.subject')}
          <input
            name="subject" placeholder={t('contact.place.subject')}
            value={data.subject} onChange={e=>setData(s=>({...s, subject: e.target.value}))}
          />
        </label>

        <label className="full">
          {t('contact.message')}*
          <textarea
            name="message" placeholder={t('contact.place.message')}
            value={data.message} onChange={e=>setData(s=>({...s, message: e.target.value}))}
            required
          />
        </label>

        {/* ФАЙЛЫ */}
        <div className="full">
          <Drop
            onDragOver={(e)=>{e.preventDefault(); setDrag(true);}}
            onDragLeave={()=>setDrag(false)}
            onDrop={onDrop}
            className={drag ? 'drag': ''}
          >
            <div><b>📎 {t('contact.attach')}</b></div>
            <div className="hint">
              {t('contact.accepted')}: png, jpg, webp, pdf, zip, dwg, dxf… — {t('contact.maxFiles', {n: MAX_FILES})}, ≤ {MAX_SIZE_MB}MB {t('contact.perFile')}
            </div>
            <div>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={()=>inputRef.current?.click()}
                aria-label={t('contact.choose')}
                style={{padding:'8px 12px', borderRadius: 999}}
              >
                {t('contact.choose')}
              </button>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={acceptStr}
                onChange={(e)=>onPick(e.target.files)}
              />
            </div>

            {!!files.length && (
              <div className="files" aria-live="polite">
                {files.map((f,i)=>(
                  <span className="chip" key={i}>
                    <b>{f.name}</b>
                    <small>({(f.size/1024/1024).toFixed(2)}MB)</small>
                    <button type="button" onClick={()=>removeFile(i)} aria-label={t('contact.removeFile')}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </Drop>
        </div>

        {/* ПОЛИТИКА */}
        <label className="full" style={{display:'flex', alignItems:'center', gap:10}}>
          <input type="checkbox" checked={data.agree} onChange={e=>setData(s=>({...s, agree: e.target.checked}))}/>
          <span className="hint">{t('contact.policy')}</span>
        </label>

        {/* ФУТЕР */}
        <div className="full">
          <Footer>
            <div aria-live="polite">
              {note?.type==='error' && <div className="error">⚠️ {note.text}</div>}
              {note?.type==='ok'    && <div className="ok">✅ {note.text}</div>}
            </div>
            <Submit type="submit" disabled={busy}>
              {busy ? t('contact.sending') : t('contact.send')}
            </Submit>
          </Footer>
        </div>
      </Body>
    </Wrap>
  );
}
